import $ from 'static/script/libs/jquery';
import dayjs from 'dayjs';
import observable from 'static/script/app/ui/checkout2/context/observable';
import modal from 'static/script/app/ui/checkout2/context/modal';
import util from 'static/script/app/ui/checkout2/context/util';
import { reload } from 'tradera-utils/location';
import { whenInitialized } from 'tradera-lang/boot-languages';

let payment = {};

// This should be fine since all code within this file is triggered async from initial rendering flow,
// and the whenInitialized-promise is resolved immediately
let t = key => key;
whenInitialized()
    .then(({ t: translate }) => (t = translate))
    .catch(() => {});

observable.on('payment-confirmed-by-user', function(
    paymentType,
    paymentResponse
) {
    const dialogText = paymentResponse.successMessage
        ? paymentResponse.successMessage
        : t('checkout_payment_confirmedByUserSuccessText');
    const buttonText = paymentResponse.successButtonText
        ? paymentResponse.successButtonText
        : t('checkout_payment_confirmedByUserSuccessButton');

    modal.showModal('checkout-modal', {
        title: t('checkout_payment_confirmedByUserSuccessTitle'),
        text: dialogText,
        nonDismissible: true,
        showSuccessIcon: true,
        buttons: {
            secondary: {
                title: buttonText,
                click: function() {
                    payment.successAction();
                }
            }
        }
    });
});

observable.on('payment-confirmed-by-user-failed', function(
    paymentType,
    failureMessage
) {
    modal.showModal('checkout-modal', {
        title: t('checkout_payment_confirmedByUserFailedTitle'),
        text: failureMessage
            ? failureMessage
            : t('checkout_payment_confirmedByUserFailedText'),
        showErrorIcon: true,
        buttons: {
            secondary: {
                title: t('checkout_payment_confirmedByUserFailedButton')
            }
        }
    });
});

observable.on('payment-type-selected', function(type) {
    payment.selectedPaymentType = type;
    observable.trigger('payment-valid');
});

observable.on('payment-valid', function() {
    if (payment.selectedPaymentType) {
        payment.isCheckoutValid = true;
    }
});

observable.on('payment-not-valid', function() {
    payment.isCheckoutValid = false;
});

observable.on('braintree-payment-ready-for-submit', function(payload) {
    if (payment.deferedPrepare) {
        payment.deferedPrepare.resolve(payload);
    }
});

observable.on('braintree-payment-not-ready-for-submit', function(isAborted) {
    if (payment.deferedPrepare) {
        payment.deferedPrepare.reject(isAborted);
    }
});

observable.on('payment-set-payload', function(payload) {
    payment.paymentPayload = payload;
});

observable.on('payment-swish-app2app-sms', payload => {
    const onsuccess = data => {
        observable.trigger('payment-swish-app2app-sms-sent', data);
    };

    $.ajax({
        type: 'POST',
        url: '/shopping/swishapp2app/sendsms',
        contentType: 'application/json; charset=utf-8',
        data: JSON.stringify(payload),
        success: onsuccess
    });
});

payment.initTimeoutHandling = function(timeout) {
    payment.stopTimeoutHandler();
    payment.timeoutHandlerId = setTimeout(function() {
        if (modal.currentModalModel.showProgressIcon) {
            util.serverLog({ counterName: 'confirm.timeout' });
            modal.showModal('checkout-modal', {
                title: t('checkout_payment_TimeOutTitle'),
                text: t('checkout_payment_TimeOutText'),
                showErrorIcon: true,
                buttons: {
                    secondary: {
                        title: t('checkout_payment_TimeOutButton'),
                        click: function() {
                            reload(false);
                        }
                    }
                }
            });
        }
    }, timeout);
};

payment.stopTimeoutHandler = function() {
    if (payment.timeoutHandlerId) {
        clearTimeout(payment.timeoutHandlerId);
    }
};

payment.performPayment = (model, url) => {
    if (!payment.isCheckoutValid) {
        return;
    }

    let modalDialog = modal.showModal('checkout-modal', {
        title: t('checkout_payment_performPaymentTitle'),
        nonDismissible: true,
        showProgressIcon: true
    });

    payment
        .preparePaymentForSubmit()
        .done(payment.confirmPurchase.bind(this, modalDialog, model, url))
        .fail(function(isAborted) {
            if (isAborted) {
                modalDialog.close();
            } else {
                modalDialog.showError(
                    t('checkout_payment_performPaymentErrorTitle'),
                    t('checkout_payment_performPaymentErrorMessage')
                );
            }
        });
};

payment.preparePaymentForSubmit = function() {
    payment.deferedPrepare = $.Deferred();

    if (
        payment.selectedPaymentType === 'card' ||
        payment.selectedPaymentType === 'paypal'
    ) {
        observable.trigger('braintree-payment-prepare-for-submit');
    } else {
        payment.deferedPrepare.resolve(payment.paymentPayload);
    }

    return payment.deferedPrepare.promise();
};

payment.confirmPurchase = function(modalDialog, model, url, paymentPayload) {
    payment.initTimeoutHandling(120000);
    model.payment = paymentPayload;
    model.paymentType = payment.selectedPaymentType;

    $.ajax({
        type: 'POST',
        url: url,
        contentType: 'application/json; charset=utf-8',
        data: JSON.stringify(model),
        success: payment.purchaseSuccess.bind(this, modalDialog),
        error: payment.purchaseFailed.bind(this, modalDialog)
    });
};

payment.purchaseSuccess = function(modalDialog, e) {
    payment.stopTimeoutHandler();

    if (e.success) {
        if (e.paymentRequiresUserConfirmation) {
            payment.successAction = function() {
                util.redirectTo(e.redirectUrl);
            };
            observable.trigger(
                'payment-requires-user-confirmation',
                payment.selectedPaymentType,
                e
            );
        } else if (e.suppressMessage) {
            util.redirectTo(e.redirectUrl);
        } else {
            modalDialog.showSuccess(
                t('checkout_payment_purchaseSuccessTitle'),
                e.successMessage
                    ? e.successMessage
                    : t('checkout_payment_purchaseSuccessText'),
                e.successButtonText,
                function() {
                    util.redirectTo(e.redirectUrl);
                }
            );
        }
    } else {
        if (
            e.paymentFailure &&
            e.paymentFailure.failureResolutionCode === 'ContactYourBank'
        ) {
            modalDialog.showError(
                t('checkout_payment_purchaseFailContactYourBankTitle'),
                t('checkout_payment_purchaseFailContactYourBankText')
            );
        } else if (
            e.paymentFailure &&
            e.paymentFailure.failureResolutionCode === 'OngoingSwishPayment'
        ) {
            let fiveMinutesFromNow = dayjs()
                .add(5, 'minutes')
                .format('HH:mm');
            modalDialog.showError(
                t('checkout_payment_purchaseFailOngoingSwishPaymentTitle'),
                t('checkout_payment_purchaseFailOngoingSwishPaymentText', {
                    fiveMinutesFromNow
                })
            );
        } else if (e.redirectUrl && !e.errorMessage) {
            modalDialog.close();
            util.redirectTo(e.redirectUrl);
        } else {
            modalDialog.showError(
                e.errorTitle
                    ? e.errorTitle
                    : t('checkout_payment_purchaseFailGeneralPaymentTitle'),
                e.errorMessage
                    ? e.errorMessage
                    : t('checkout_payment_purchaseFailGeneralPaymentText')
            );
        }

        modalDialog.buttons.secondary.click = function() {
            if (e.redirectUrl) {
                util.redirectTo(e.redirectUrl);
            }

            modalDialog.close();

            if (e.paymentFailure) {
                observable.trigger(
                    'payment-failed',
                    e.paymentFailure,
                    payment.selectedPaymentType
                );
            }
        };
    }
};

payment.purchaseFailed = function(modalDialog, e) {
    payment.stopTimeoutHandler();
    modalDialog.showError(
        t('checkout_payment_purchaseFailedTitle'),
        t('checkout_payment_purchaseFailedText')
    );
    util.serverLog({
        error: 'purchaseFailed: ' + JSON.stringify(e)
    });
};

export default payment;
